import React, { Dispatch, Fragment, ReactElement, SetStateAction, useMemo, useState } from 'react';
import Button from '@cmsgov/design-system-core/dist/components/Button/Button';
import { FormattedMessage } from 'react-intl/dist';

// Components

import Form from '../../../lib/wrappers/Form';
import ModalHeader from '../../../components/common/headers/ModalHeader';
import UniversalInput from '../../../lib/wrappers/UniversalInput';

// Utils

import { normalizeNameOfPerson } from '../../../utils/normalize/normalizeNameOfPerson';

// Constants

import { DROPDOWN_OPTIONS } from '../../../constants/ssap/hhmTypes/dropdownOptions';
import { INPUT_TYPES } from '../../../constants/inputs/inputTypes';
import { VALIDATION_TYPES } from '../../../constants/inputs/validationTypes';

// Types

import { CommonModalProps, OnChangeArgTypes, PersonName } from '../../../components/ssap/SsapTypes';

interface DeceasedMemberModal extends CommonModalProps {
    modal: {
        modalData: {
            actions: {
                handleMemberDeletion: Function
            },
            coverageYear: number,
            personName: PersonName
        }
    }
}


const DeceasedMemberModal = (props: DeceasedMemberModal): ReactElement | null => {

    const {
        actions: {
            hideModal
        },
        bottomAnchors: [
            anchor_1,
        ],
        boundAnchor,
        formName = '',
        modal: {
            modalData: {
                actions: {
                    handleMemberDeletion
                },
                coverageYear,
                personName
            }
        },
        modalHeaderProps
    } = props;

    interface LocalState {
        removeAction: boolean | null
    }

    const INITIAL_STATE: LocalState = {
        removeAction: false
    };

    const [
        localState,
        updateLocalState
    ]: [
        LocalState,
        Dispatch<SetStateAction<LocalState>>
    ] = useState<LocalState>(INITIAL_STATE);

    const {
        removeAction
    }: LocalState = localState;

    const deceasedEarlierMemberName: string = useMemo(
        (): string => normalizeNameOfPerson(personName),
        [ personName ]
    );

    const onFieldChange = (...args: OnChangeArgTypes): void => {
        const [ , , , [ name, value ] ] = args;

        updateLocalState((state: LocalState): LocalState => ({
            ...state,
            [ name ]: value
        }));
    };

    const handleHideClick = (): void => hideModal();

    const handleSaveClick = (): void => {
        if(!removeAction) {
            handleMemberDeletion();
        }
        hideModal();
    };

    return (
        <Fragment>
            <div className = 'gi-modal__header__head' >
                <ModalHeader { ...modalHeaderProps } />
            </div>

            <div id = 'deceasedMemberModal' className = 'gi-modal__body' >
                <div className = 'usa-alert usa-alert--info gi-alert gi-alert__info' >
                    <div className = 'usa-alert__body gi-alert__info__body' >
                        <p className = 'usa-alert__text gi-alert__info__text' >
                            <FormattedMessage
                                id = 'modals.deceased_member.death_date_before_coverage_year'
                                values = { {
                                    coverageYear
                                } }
                            />
                        </p>
                    </div>
                </div>

                <Form { ...{ formName } } >
                    <UniversalInput
                        legend = { {
                            legendText: 'ssap.input.legend.deceased_member_options',
                            legendTextReplacements: {
                                personName: <strong>{ deceasedEarlierMemberName }</strong>
                            }
                        } }
                        label = { {
                            showRequiredIcon: true,
                            ulClass: [ 'margin-b-20' ]
                        } }
                        inputData = { {
                            isInputRequired: true,
                            type: INPUT_TYPES.RADIO_BUTTONS,
                            liClass: [ 'text-left' ],
                            name: 'removeAction',
                            value: removeAction,
                            options: DROPDOWN_OPTIONS.DECEASED_MEMBER_ACTIONS,
                            optionsReplacements: {
                                personName: deceasedEarlierMemberName,
                            },
                            validationType: VALIDATION_TYPES.CHOSEN,
                            currentData: localState,
                            fields: [ ]
                        } }
                        errorClasses={ [ 'margin-l-0' ] }
                        inputActions={ {
                            onFieldChange
                        } }
                    />
                </Form>
            </div>

            <div className = 'gi-modal__footer' >
                <div className = 'grid-row controls' >
                    <div className = 'grid-offset-4 txt-left' >
                        <Button
                            className = 'usa-button usa-button--outline gi-button gi-button__outline'
                            onClick = { handleHideClick }
                        >
                            <FormattedMessage id = 'button.cancel' />
                        </Button>
                        <Button
                            className = 'usa-button gi-button gi-button__primary'
                            onClick = { handleSaveClick }
                            inputRef = { boundAnchor(anchor_1) }
                        >
                            <FormattedMessage id = 'button.save_continue' />
                        </Button>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default DeceasedMemberModal;